import { toast } from 'react-toastify';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
// utils
import axiosInstance from './axios';
// types
import {
  getConsolidateSalesAndReturnReportResponse,
  GetCustomerReportConsolidatedReportRequest,
  GetCustomerRequest,
  GetCustomersResponse,
  TDownloadCustomerRequest,
} from '../types/customerTypes';

// Infinite get all customers
export const useInfiniteGetAllCustomer = (postData: GetCustomerRequest) => {
  return useInfiniteQuery({
    queryKey: ['useInfiniteGetAllCustomer', postData],
    queryFn: async () => {
      const { data } = await axiosInstance.post('/customer/all', {
        ...postData,
      });
      return data as GetCustomersResponse & { hasNextPage?: boolean };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length + 1 : undefined;
      return nextPage;
    },
    initialPageParam: 1,
  });
};

// get all customers
export const useGetAllCustomer = (postData: GetCustomerRequest) => {
  return useQuery({
    queryKey: ['useGetAllCustomer', postData],
    queryFn: async () => {
      const { data } = await axiosInstance.post('/customer/all', {
        ...postData,
      });
      return data as GetCustomersResponse;
    },
  });
};

// Add new customer
export const useAddCustomer = () => {
  return useMutation({
    mutationKey: ['useAddCustomer'],
    mutationFn: async (postData: any) => {
      const { data } = await axiosInstance.post('/customer', postData);
      return data;
    },
  });
};

// Get a customer by email
export const useGetACustomer = ({ customerId }: { customerId: string }) => {
  return useQuery({
    queryKey: ['useGetACustomer', customerId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/customer/${customerId}`);
      return data as any;
    },
    enabled: !!customerId,
  });
};

// Update customer
export const useUpdateCustomer = () => {
  return useMutation({
    mutationKey: ['useUpdateCustomer'],
    mutationFn: async (postData: any) => {
      const { data } = await axiosInstance.put('/customer', postData);
      return data as any;
    },
  });
};

// Delete customer
export const useDelCustomer = () => {
  return useMutation({
    mutationKey: ['useDelCustomer'],
    mutationFn: async (id: string | number) => {
      const { data } = await axiosInstance.delete(`/customer/${id}`);
      return data;
    },
  });
};

export const useGetPaymentSlip = (options = {}) => {
  return useMutation({
    mutationKey: ['useGetPaymentSlip'],
    mutationFn: async ({ id }: { id: string }) => {
      const { data } = await axiosInstance.get(`/pdf/payment-slip/${id}`);
      return data;
    },
    ...options,
  });
};

// download customer history

export const useDownloadAllInvoice = () => {
  return useMutation({
    mutationKey: ['downloadAllInvoice'],
    mutationFn: async (postData: TDownloadCustomerRequest) => {
      const { data } = await axiosInstance.post(
        '/sales/invoice/all',
        postData,
        {
          responseType: 'blob',
        }
      );
      return data;
    },
    onSuccess(blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `all-Invoice.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded successfully');
    },
  });
};

// get consolidated report data customer

export const useGetAllConsolidateSalesAndReturnReport = (
  postData: GetCustomerReportConsolidatedReportRequest
) => {
  return useQuery({
    queryKey: ['useGetAllConsolidateSalesAndReturnReport', postData],
    queryFn: async () => {
      const { data } = await axiosInstance.post(
        `/reports/getConsolidateSalesAndReturnReport`,
        postData
      );

      return data as getConsolidateSalesAndReturnReportResponse;
    },
  });
};

export const useGetCustomerPaymentHistory = (params: any) => {
  const { customerId, ...queryParams } = params;

  return useQuery({
    queryKey: ['useGetCustomerPaymentHistory', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/customer/${customerId}/payments`, {
        params: queryParams,
      });

      return data;
    },
    enabled: Boolean(customerId && /^\d+$/.test(customerId)),
  });
};

export const useDownloadCustomerPaymentHistory = () => {
  return useMutation({
    mutationKey: ['useDownloadCustomerPaymentHistory'],
    mutationFn: async (customerId: number | string) => {
      const { data } = await axiosInstance.get(
        `/customer/${customerId}/payments/export/excel`,
        {
          responseType: 'blob',
        }
      );
      return data;
    },
    onSuccess: (blob, customerId) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customer_${customerId}_payment_history.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};


